// /var/www/oszczedzam/client/src/components/reports/MonthlyComparisonChart.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, Grid, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Card, CardContent, Divider
} from '@mui/material';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, 
  ResponsiveContainer, Cell 
} from 'recharts';
import { useTheme } from '@mui/material/styles';

function MonthlyComparisonChart({ transactions = [], filters }) {
  const theme = useTheme();
  const [chartData, setChartData] = useState([]);
  const [summary, setSummary] = useState({
    currentPeriod: { income: 0, expenses: 0, balance: 0 },
    comparisonPeriod: { income: 0, expenses: 0, balance: 0 },
    differences: { income: 0, expenses: 0, balance: 0 },
    percentages: { income: 0, expenses: 0, balance: 0 }
  });

  // Przygotuj dane do wykresu i podsumowanie na podstawie filtrów
  useEffect(() => {
    if (transactions && transactions.length > 0) {
      // Pobierz dane dla bieżącego okresu
      const currentPeriodData = filterTransactionsByPeriod(transactions, filters, 'current');
      
      // Pobierz dane dla okresu porównawczego
      const comparisonPeriodData = filterTransactionsByPeriod(transactions, filters, 'comparison');
      
      // Przygotuj dane do wykresu
      prepareChartData(currentPeriodData, comparisonPeriodData);
      
      // Oblicz podsumowanie
      calculateSummary(currentPeriodData, comparisonPeriodData);
    } else {
      // Zresetuj dane, jeśli nie ma transakcji
      setChartData([]);
      setSummary({
        currentPeriod: { income: 0, expenses: 0, balance: 0 },
        comparisonPeriod: { income: 0, expenses: 0, balance: 0 },
        differences: { income: 0, expenses: 0, balance: 0 },
        percentages: { income: 0, expenses: 0, balance: 0 }
      });
    }
  }, [transactions, filters]);

  // Filtruj transakcje według wybranego okresu
  const filterTransactionsByPeriod = (transactions, filters, periodType) => {
    if (!transactions || !filters) return [];
    
    const { period, year, month, compareWith } = filters;
    let filteredYear = year;
    let filteredMonth = month;
    
    // Określ rok i miesiąc dla okresu porównawczego
    if (periodType === 'comparison') {
      if (compareWith === 'previous') {
        if (period === 'month') {
          // Poprzedni miesiąc
          if (month === 0) {
            filteredMonth = 11;
            filteredYear = year - 1;
          } else {
            filteredMonth = month - 1;
          }
        } else if (period === 'year') {
          // Poprzedni rok
          filteredYear = year - 1;
        }
      } else if (compareWith === 'last-year') {
        // Ten sam okres, rok temu
        filteredYear = year - 1;
      }
    }
    
    // Filtruj transakcje według daty
    return transactions.filter(transaction => {
      if (!transaction.date) return false;
      
      const date = new Date(transaction.date);
      
      if (period === 'month') {
        return date.getFullYear() === filteredYear && date.getMonth() === filteredMonth;
      } else if (period === 'year') {
        return date.getFullYear() === filteredYear;
      }
      
      return false;
    });
  };

  // Przygotuj dane do wykresu
  const prepareChartData = (currentPeriodData, comparisonPeriodData) => {
    // Ochrona przed null lub undefined
    if (!currentPeriodData || !comparisonPeriodData) {
      setChartData([]);
      return;
    }
    
    // Pogrupuj transakcje według kategorii
    const categories = new Set();
    
    // Dodaj wszystkie kategorie z obu okresów
    currentPeriodData.forEach(t => t.category && categories.add(t.category));
    comparisonPeriodData.forEach(t => t.category && categories.add(t.category));
    
    // Przygotuj dane dla każdej kategorii
    const data = Array.from(categories).map(category => {
      // Suma wydatków dla bieżącego okresu
      const currentExpenses = currentPeriodData
        .filter(t => t.category === category && t.amount < 0)
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);
      
      // Suma wydatków dla okresu porównawczego
      const comparisonExpenses = comparisonPeriodData
        .filter(t => t.category === category && t.amount < 0)
        .reduce((sum, t) => sum + Math.abs(t.amount), 0);
      
      return {
        name: category,
        obecny: currentExpenses,
        poprzedni: comparisonExpenses,
        różnica: currentExpenses - comparisonExpenses
      };
    });
    
    // Sortuj kategorie według wydatków w bieżącym okresie (malejąco)
    data.sort((a, b) => b.obecny - a.obecny);
    
    setChartData(data);
  };

  // Oblicz podsumowanie finansowe
  const calculateSummary = (currentPeriodData, comparisonPeriodData) => {
    // Ochrona przed null lub undefined
    if (!currentPeriodData || !comparisonPeriodData) {
      setSummary({
        currentPeriod: { income: 0, expenses: 0, balance: 0 },
        comparisonPeriod: { income: 0, expenses: 0, balance: 0 },
        differences: { income: 0, expenses: 0, balance: 0 },
        percentages: { income: 0, expenses: 0, balance: 0 }
      });
      return;
    }
    
    // Obliczenia dla bieżącego okresu
    const currentIncome = currentPeriodData
      .filter(t => t.amount > 0)
      .reduce((sum, t) => sum + t.amount, 0);
    
    const currentExpenses = currentPeriodData
      .filter(t => t.amount < 0)
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);
    
    const currentBalance = currentIncome - currentExpenses;
    
    // Obliczenia dla okresu porównawczego
    const comparisonIncome = comparisonPeriodData
      .filter(t => t.amount > 0)
      .reduce((sum, t) => sum + t.amount, 0);
    
    const comparisonExpenses = comparisonPeriodData
      .filter(t => t.amount < 0)
      .reduce((sum, t) => sum + Math.abs(t.amount), 0);
    
    const comparisonBalance = comparisonIncome - comparisonExpenses;
    
    // Oblicz różnice
    const incomeDiff = currentIncome - comparisonIncome;
    const expensesDiff = currentExpenses - comparisonExpenses;
    const balanceDiff = currentBalance - comparisonBalance;
    
    // Oblicz procentowe różnice
    const incomePercentage = comparisonIncome !== 0 
      ? (incomeDiff / comparisonIncome) * 100 
      : currentIncome > 0 ? 100 : 0;
    
    const expensesPercentage = comparisonExpenses !== 0 
      ? (expensesDiff / comparisonExpenses) * 100 
      : currentExpenses > 0 ? 100 : 0;
    
    const balancePercentage = comparisonBalance !== 0 
      ? (balanceDiff / Math.abs(comparisonBalance)) * 100 
      : currentBalance !== 0 ? 100 : 0;
    
    setSummary({
      currentPeriod: { income: currentIncome, expenses: currentExpenses, balance: currentBalance },
      comparisonPeriod: { income: comparisonIncome, expenses: comparisonExpenses, balance: comparisonBalance },
      differences: { income: incomeDiff, expenses: expensesDiff, balance: balanceDiff },
      percentages: { income: incomePercentage, expenses: expensesPercentage, balance: balancePercentage }
    });
  };

  // Formatuj kwotę
  const formatAmount = (amount) => {
    return amount.toLocaleString('pl-PL', { style: 'currency', currency: 'PLN' });
  };
  
  // Formatuj procent
  const formatPercentage = (percentage) => {
    return percentage.toFixed(1) + '%';
  };
  
  // Formatuj nazwę okresu
  const formatPeriodName = (filters, isComparison = false) => {
    if (!filters) return '';
    
    const { period, year, month, compareWith } = filters;
    const months = ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 
                   'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'];
    
    if (!isComparison) {
      // Bieżący okres
      if (period === 'month') {
        return `${months[month]} ${year}`;
      } else {
        return `Rok ${year}`;
      }
    } else {
      // Okres porównawczy
      if (compareWith === 'previous') {
        if (period === 'month') {
          let prevMonth = month - 1;
          let prevYear = year;
          
          if (prevMonth < 0) {
            prevMonth = 11;
            prevYear = year - 1;
          }
          
          return `${months[prevMonth]} ${prevYear}`;
        } else {
          return `Rok ${year - 1}`;
        }
      } else if (compareWith === 'last-year') {
        if (period === 'month') {
          return `${months[month]} ${year - 1}`;
        } else {
          return `Rok ${year - 1}`;
        }
      }
    }
    
    return '';
  };

  // Custom tooltip dla wykresu
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper elevation={3} sx={{ p: 2, backgroundColor: 'white' }}>
          <Typography variant="subtitle2">{label}</Typography>
          {payload.map((entry, index) => (
            <Box key={`item-${index}`} sx={{ color: entry.color, display: 'flex', alignItems: 'center', my: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: entry.color, mr: 1 }} />
              <Typography variant="body2">
                {entry.name}: {formatAmount(entry.value)}
              </Typography>
            </Box>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Porównanie: {formatPeriodName(filters)} vs {formatPeriodName(filters, true)}
        </Typography>
        
        {/* Podsumowanie porównawcze */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  Przychody
                </Typography>
                <Typography variant="h5" component="div" color="success.main">
                  {formatAmount(summary.currentPeriod.income)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    vs {formatAmount(summary.comparisonPeriod.income)}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color={summary.differences.income >= 0 ? 'success.main' : 'error.main'}
                    fontWeight="bold"
                  >
                    {summary.differences.income >= 0 ? '+' : ''}
                    {formatAmount(summary.differences.income)} 
                    ({summary.percentages.income >= 0 ? '+' : ''}
                    {formatPercentage(summary.percentages.income)})
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  Wydatki
                </Typography>
                <Typography variant="h5" component="div" color="error.main">
                  {formatAmount(summary.currentPeriod.expenses)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    vs {formatAmount(summary.comparisonPeriod.expenses)}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color={summary.differences.expenses <= 0 ? 'success.main' : 'error.main'}
                    fontWeight="bold"
                  >
                    {summary.differences.expenses >= 0 ? '+' : ''}
                    {formatAmount(summary.differences.expenses)} 
                    ({summary.percentages.expenses >= 0 ? '+' : ''}
                    {formatPercentage(summary.percentages.expenses)})
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                  Bilans
                </Typography>
                <Typography 
                  variant="h5" 
                  component="div" 
                  color={summary.currentPeriod.balance >= 0 ? 'success.main' : 'error.main'}
                >
                  {formatAmount(summary.currentPeriod.balance)}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    vs {formatAmount(summary.comparisonPeriod.balance)}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color={summary.differences.balance >= 0 ? 'success.main' : 'error.main'}
                    fontWeight="bold"
                  >
                    {summary.differences.balance >= 0 ? '+' : ''}
                    {formatAmount(summary.differences.balance)} 
                    ({summary.percentages.balance >= 0 ? '+' : ''}
                    {formatPercentage(summary.percentages.balance)})
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Wykres porównawczy wydatków według kategorii */}
        <Typography variant="subtitle1" gutterBottom>
          Porównanie wydatków według kategorii
        </Typography>
        <Box sx={{ height: 400, mb: 4 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip content={<CustomTooltip />} />
              <Legend />
              <Bar 
                dataKey="obecny" 
                name={`Obecny (${formatPeriodName(filters)})`} 
                fill={theme.palette.primary.main} 
              />
              <Bar 
                dataKey="poprzedni" 
                name={`Poprzedni (${formatPeriodName(filters, true)})`} 
                fill={theme.palette.secondary.main} 
              />
            </BarChart>
          </ResponsiveContainer>
        </Box>
        
        {/* Tabela szczegółowa */}
        <Typography variant="subtitle1" gutterBottom>
          Szczegółowe porównanie kategorii
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Kategoria</TableCell>
                <TableCell align="right">{formatPeriodName(filters)}</TableCell>
                <TableCell align="right">{formatPeriodName(filters, true)}</TableCell>
                <TableCell align="right">Różnica</TableCell>
                <TableCell align="right">Zmiana %</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chartData.map((row) => {
                // Oblicz procentową zmianę
                const percentChange = row.poprzedni !== 0
                  ? ((row.obecny - row.poprzedni) / row.poprzedni) * 100
                  : row.obecny > 0 ? 100 : 0;
                
                return (
                  <TableRow key={row.name}>
                    <TableCell component="th" scope="row">
                      {row.name}
                    </TableCell>
                    <TableCell align="right">{formatAmount(row.obecny)}</TableCell>
                    <TableCell align="right">{formatAmount(row.poprzedni)}</TableCell>
                    <TableCell 
                      align="right"
                      sx={{ 
                        color: row.różnica <= 0 ? 'success.main' : 'error.main',
                        fontWeight: Math.abs(row.różnica) > 50 ? 'bold' : 'normal'
                      }}
                    >
                      {row.różnica >= 0 ? '+' : ''}{formatAmount(row.różnica)}
                    </TableCell>
                    <TableCell 
                      align="right"
                      sx={{ 
                        color: percentChange <= 0 ? 'success.main' : 'error.main',
                        fontWeight: Math.abs(percentChange) > 20 ? 'bold' : 'normal'
                      }}
                    >
                      {percentChange >= 0 ? '+' : ''}{percentChange.toFixed(1)}%
                    </TableCell>
                  </TableRow>
                );
              })}
              
              {/* Wiersz podsumowujący */}
              {chartData.length > 0 && (
                <TableRow>
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    SUMA WYDATKÓW
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatAmount(chartData.reduce((sum, cat) => sum + cat.obecny, 0))}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatAmount(chartData.reduce((sum, cat) => sum + cat.poprzedni, 0))}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: summary.differences.expenses <= 0 ? 'success.main' : 'error.main'
                    }}
                  >
                    {summary.differences.expenses >= 0 ? '+' : ''}
                    {formatAmount(summary.differences.expenses)}
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: summary.percentages.expenses <= 0 ? 'success.main' : 'error.main'
                    }}
                  >
                    {summary.percentages.expenses >= 0 ? '+' : ''}
                    {formatPercentage(summary.percentages.expenses)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}

export default MonthlyComparisonChart;